import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Loader2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { Document, DocumentListResponse } from "@/types/document";
import { STATUS_COLORS } from "@/types/document";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

interface Props {
  nodeId: string;
  /**
   * "admin" (default): authenticated endpoint + internal `/documents/{id}` links.
   * "public": unauthenticated `/public/taxonomy/...` endpoint + file-preview links
   *           (used by the public /taksonomi explorer page).
   */
  variant?: "admin" | "public";
}

/**
 * Read-only listing of documents linked EXACTLY to the selected taxonomy node
 * (flat list, tanpa pemisahan grup).
 *
 * Pulls from `GET /taxonomy/{id}/documents?include_descendants=false` — filter
 * PERSIS pada node terpilih (`t_dokumen.taxonomy_node_id` = node ini, atau link
 * m2m ke node ini). Node anak TIDAK ikut: mis. root "PNBP" hanya menampilkan
 * dokumen yang klasifikasi primernya tepat di root (regulasi PNBP umum).
 */
export default function DokumenTerkaitView({
  nodeId,
  variant = "admin",
}: Props) {
  const isPublic = variant === "public";
  const [activeBentukFilters, setActiveBentukFilters] = useState<Set<string>>(
    new Set(),
  );

  const { data, isLoading } = useQuery<DocumentListResponse>({
    queryKey: ["taxonomy-node-documents", variant, nodeId],
    queryFn: () =>
      api.get(
        isPublic
          ? `/public/taxonomy/${nodeId}/documents`
          : `/taxonomy/${nodeId}/documents`,
        { include_descendants: false, page_size: 100 },
      ),
    enabled: !!nodeId,
  });

  // Filter chips derived from the unique bentuk_dokumen values in the result.
  const bentukOptions = useMemo(() => {
    if (!data?.items) return [];
    const map = new Map<string, string>();
    for (const d of data.items) {
      if (d.bentuk_dokumen_nama && !map.has(d.bentuk_dokumen_nama)) {
        map.set(d.bentuk_dokumen_nama, d.bentuk_dokumen_kode);
      }
    }
    return [...map.entries()].map(([nama, kode]) => ({ nama, kode }));
  }, [data]);

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (activeBentukFilters.size === 0) return data.items;
    return data.items.filter((d) =>
      activeBentukFilters.has(d.bentuk_dokumen_nama),
    );
  }, [data, activeBentukFilters]);

  const toggleBentukFilter = (nama: string) => {
    setActiveBentukFilters((prev) => {
      const next = new Set(prev);
      if (next.has(nama)) next.delete(nama);
      else next.add(nama);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat dokumen …
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
        <FileText className="h-8 w-8 mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-600 font-medium">
          Belum ada dokumen yang ter-link ke node ini.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Tag dokumen ke node taxonomy lewat halaman edit dokumen.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary + filter chips */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-700">
            {filteredItems.length}
          </span>{" "}
          dari <span className="font-medium">{data.total}</span> dokumen
          {activeBentukFilters.size > 0 && (
            <button
              onClick={() => setActiveBentukFilters(new Set())}
              className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-0.5"
            >
              <X className="h-3 w-3" /> reset filter
            </button>
          )}
        </p>
      </div>

      {bentukOptions.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs uppercase tracking-wide text-gray-400 self-center mr-1">
            Filter:
          </span>
          {bentukOptions.map(({ nama, kode }) => {
            const active = activeBentukFilters.has(nama);
            return (
              <button
                key={nama}
                type="button"
                onClick={() => toggleBentukFilter(nama)}
                className={`rounded-full border px-3 py-0.5 text-xs transition ${
                  active
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <span className="font-mono opacity-70 mr-1">{kode}</span>
                {nama}
              </button>
            );
          })}
        </div>
      )}

      {/* Accordion groups — per Jenis PNBP */}
      {/* Daftar rata — semua dokumen dengan Jenis PNBP yang sesuai node */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <ul className="divide-y divide-gray-100">
          {filteredItems.map((d) => (
            <li
              key={d.id}
              className="px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {isPublic ? (
                    <a
                      href={`${API_BASE}/public/dokumen/${d.id}/preview`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 break-words"
                    >
                      {d.judul}
                    </a>
                  ) : (
                    <Link
                      to={`/documents/${d.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 break-words"
                    >
                      {d.judul}
                    </Link>
                  )}
                  {d.nomor_dokumen && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {d.nomor_dokumen}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className="rounded px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 font-mono">
                      {d.bentuk_dokumen_kode} · {d.bentuk_dokumen_nama}
                    </span>
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[d.status_kode]}`}
                >
                  {d.status_nama}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
